import React, { useEffect, useMemo } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Checkbox, TextField, FormControlLabel,
    Grid, Typography, Box
} from '@mui/material';

interface Instrumento {
    tipo_instrumento: string;
    uuid: string;
    nome: string;
}

interface InstrumentoConfig extends Instrumento {
    selecionado: boolean;
    valor: string;
}

interface ModalConfigGraficoProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (configData: Record<string, number>) => void;
    instrumentos: Instrumento[];
}

const tipoMap: Record<string, string> = {
    'piezometro': 'Piezômetro',
    'regua': 'Régua de reservatório',
    'indicador': 'Indicador de nível de água',
};

const agruparPorTipo = (instrumentos: InstrumentoConfig[]): Record<string, InstrumentoConfig[]> => {
    return instrumentos.reduce((grupos, instrumento) => {
        const tipo = instrumento.tipo_instrumento.split(' ')[0] || 'Outros';

        if (!grupos[tipo]) {
            grupos[tipo] = [];
        }

        grupos[tipo].push(instrumento);
        return grupos;
    }, {} as Record<string, InstrumentoConfig[]>);
};

export const ModalConfigGrafico: React.FC<ModalConfigGraficoProps> = ({
    open,
    onClose,
    onSubmit,
    instrumentos
}) => {
    const [instrumentosConfig, setInstrumentosConfig] = React.useState<InstrumentoConfig[]>([]);

    useEffect(() => {
        if (open) {
            const configs = instrumentos.map(instrumento => ({
                uuid: instrumento.uuid,
                nome: instrumento.nome || `Instrumento ${instrumento.uuid.slice(0, 5)}`,
                selecionado: false,
                valor: '',
                tipo_instrumento: instrumento.tipo_instrumento
            }));
            setInstrumentosConfig(configs);
        }
    }, [open, instrumentos]);

    const handleToggleInstrumento = (uuid: string) => {
        setInstrumentosConfig(prev => prev.map(item =>
            item.uuid === uuid ? { ...item, selecionado: !item.selecionado } : item
        ));
    };

    const handleValorChange = (uuid: string, valor: string) => {
        setInstrumentosConfig(prev => prev.map(item =>
            item.uuid === uuid ? { ...item, valor } : item
        ));
    };

    const handleSubmit = () => {
        const configData: Record<string, number> = {};

        instrumentosConfig.forEach(item => {
            if (item.selecionado && item.valor && !isNaN(parseFloat(item.valor))) {
                configData[item.uuid] = parseFloat(item.valor);
            }
        });

        onSubmit(configData);
    };

    const instrumentosAgrupados = useMemo(() => agruparPorTipo(instrumentosConfig), [instrumentosConfig]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>Configurar Gráfico</DialogTitle>
            <DialogContent>
                <Typography variant="subtitle1" gutterBottom>
                    Selecione os instrumentos e defina as posições em metros para o eixo x do gráfico
                </Typography>

                <Box sx={{ maxHeight: '60vh', overflow: 'auto', mt: 2 }}>
                    {Object.keys(instrumentosAgrupados).map(tipo => (
                        <Box key={tipo} sx={{ mb: 4 }}>
                            <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                                {tipoMap[tipo] || tipo}
                            </Typography>
                            <Grid container spacing={2}>
                                {instrumentosAgrupados[tipo].map(instrumento => (
                                    <Grid item xs={12} key={instrumento.uuid}>
                                        <Box display="flex" alignItems="center">
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={instrumento.selecionado}
                                                        onChange={() => handleToggleInstrumento(instrumento.uuid)}
                                                    />
                                                }
                                                label={instrumento.nome}
                                                sx={{ minWidth: 200, mr: 2 }}
                                            />
                                            <TextField
                                                size="small"
                                                type="number"
                                                label="posição em metros do eixo x"
                                                value={instrumento.valor}
                                                onChange={(e) => handleValorChange(instrumento.uuid, e.target.value)}
                                                disabled={!instrumento.selecionado}
                                                fullWidth
                                            />
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    ))}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!instrumentosConfig.some(item => item.selecionado && item.valor)}
                >
                    Aplicar Configuração
                </Button>
            </DialogActions>
        </Dialog>
    );
};